import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { DefaultPageRouteService } from '../../navigation';
import { SessionRefreshCoordinatorService } from '../../session-refresh/services/session-refresh-coordinator.service';
import { AuthenticationService, PermissionsService } from '../';

// https://github.com/jvandemo/generator-angular2-library/issues/221#issuecomment-355945207

@Injectable()
export class AuthGuard {
  private authService = inject(AuthenticationService);
  private permissionsService = inject(PermissionsService);
  private router = inject(DefaultPageRouteService);
  private readonly defaultRouteService = inject(DefaultPageRouteService);
  private readonly sessionRefreshCoordinator = inject(SessionRefreshCoordinatorService);

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.authService.isAuthorized()
      .pipe(
        map((isAuthorized: boolean) => {
          if (!isAuthorized) {
            this.authService.setInterruptedUrl(state.url);
            if (state.url !== this.defaultRouteService.getCurrentFallbackPage()) {
              this.router.navigateToPublicFallback().catch((error: unknown) => console.error(error));
            }

            return false;
          }

          return true;
        }),
        map((authorized: boolean): boolean => {
          let result = authorized;
          if (result && this.authService.isExpired()) {
            this.authService.setInterruptedUrl(state.url);
            this.sessionRefreshCoordinator.handleSessionExpired();
            result = false;
          }

          return result;
        }),
        map((authorized: boolean): boolean => {
          let result = authorized;
          if (result && route.data && route.data.allowedRoles && route.data.allowedRoles.length > 0) {
            result = this.permissionsService.hasAnyRole(route.data.allowedRoles);
            if (!result) {
              this.authService.setInterruptedUrl('');
              this.authService.logOut();
            }
          }

          return result;
        })
      );
  }

  public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.canActivate(route, state);
  }
}
